import React, { useContext } from "react";
import { motion } from "framer-motion";
import StyleContext from "../../contexts/StyleContext";
import { socialMediaLinks, contactInfo, greeting } from "../../portfolio";

export default function FooterModern() {
  const { isDark } = useContext(StyleContext);
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: "GitHub", icon: "fab fa-github", url: socialMediaLinks.github },
    { name: "LinkedIn", icon: "fab fa-linkedin", url: socialMediaLinks.linkedin },
    { name: "Twitter", icon: "fab fa-twitter", url: socialMediaLinks.twitter },
    { name: "Medium", icon: "fab fa-medium", url: socialMediaLinks.medium },
    { name: "Instagram", icon: "fab fa-instagram", url: socialMediaLinks.instagram },
  ].filter(link => link.url);

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  return (
    <footer className="relative overflow-hidden" id="contact">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900" />

      {/* Animated Orbs */}
      <motion.div
        className="absolute -left-20 top-0 w-96 h-96 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute -right-20 bottom-0 w-96 h-96 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full opacity-20 blur-3xl"
        animate={{
          scale: [1.2, 1, 1.2],
          x: [0, -50, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="relative z-10 container mx-auto px-4 py-20"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {/* Main Content */}
        <div className="max-w-6xl mx-auto">
          {/* Top Section */}
          <motion.div
            variants={itemVariants}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Let's Build Something Amazing Together
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {contactInfo.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <motion.a
                href={`mailto:${contactInfo.email_address || socialMediaLinks.gmail}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-purple-500/50 transition-all duration-300"
              >
                Get In Touch
              </motion.a>
              {greeting.resumeLink && (
                <motion.a
                  href={greeting.resumeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-white/10 backdrop-blur-xl text-white rounded-xl font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  Download Resume
                </motion.a>
              )}
            </div>
          </motion.div>

          {/* Divider */}
          <motion.div
            variants={itemVariants}
            className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-12"
          />

          {/* Social Links & Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Brand */}
            <motion.div variants={itemVariants} className="text-center md:text-left">
              <h3 className="text-2xl font-bold gradient-text mb-2">
                {greeting.username || "Abdullahi"}
              </h3>
              <p className="text-gray-400">
                Software Engineer & Tech Enthusiast
              </p>
            </motion.div>

            {/* Quick Links */}
            <motion.div variants={itemVariants} className="text-center">
              <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
              <div className="flex flex-col gap-2">
                {["Skills", "Experience", "Projects", "Blogs"].map((link) => (
                  <a
                    key={link}
                    href={`#${link.toLowerCase()}`}
                    className="text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    {link}
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={itemVariants} className="text-center md:text-right">
              <h4 className="text-lg font-semibold text-white mb-4">Contact</h4>
              <div className="flex flex-col gap-2">
                <a
                  href={`mailto:${contactInfo.email_address || socialMediaLinks.gmail}`}
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  {contactInfo.email_address || socialMediaLinks.gmail}
                </a>
                {contactInfo.number && (
                  <a
                    href={`tel:${contactInfo.number}`}
                    className="text-gray-400 hover:text-white transition-colors duration-300"
                  >
                    {contactInfo.number}
                  </a>
                )}
              </div>
            </motion.div>
          </div>

          {/* Social Icons */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center gap-4 mb-12"
          >
            {socialLinks.map((social) => (
              <motion.a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.2, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
                title={social.name}
              >
                <i className={`${social.icon} text-lg`} />
              </motion.a>
            ))}
          </motion.div>

          {/* Bottom Bar */}
          <motion.div
            variants={itemVariants}
            className="text-center pt-8 border-t border-white/10"
          >
            <p className="text-gray-400">
              © {currentYear} Abdullahi Yusuf. All Rights Reserved.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-900 to-transparent pointer-events-none" />
    </footer>
  );
}
